import React from 'react';
import styled from '@emotion/styled';
import { Button, Colors, Typography } from '@zapier/design-system';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { mediaQueries } from 'components/mediaQueries';
import backgroundArtwork from './backgroundArtwork.png';
import { Link } from 'components/Link/Link';
import {
  OptionSelected,
  TrackedComponent,
  useAvroTracking,
  withScrollTracking,
} from 'tracking';

export const ZapierProductModule = () => {
  const trackEvent = useTrackEvent();
  const { emitCTAClickEvent } = useAvroTracking();

  const buttonText = 'Sign up';

  const linkText = 'See how Zapier works';

  return (
    <TrackingWrapper
      avroIdentifier={TrackedComponent.ZapierProductModule}
      data-testid="zapier-product-module"
      preAvroIdentifier="Zapier Product Module"
    >
      <Container>
        <Content>
          <Text>
            Improve your productivity automatically. Use Zapier to get your apps
            working together.
          </Text>
          <CTAWrapper>
            <Button
              href="/sign-up"
              hasShadow={true}
              onClick={() => {
                emitCTAClickEvent({
                  interaction_raw_text: buttonText,
                  destination_url_address: '/sign-up',
                  custom_tracking_fields: {
                    component: TrackedComponent.ZapierProductModule,
                  },
                });
                trackEvent('call_to_action', {
                  path: window.location.pathname,
                  from_path: document.referrer,
                  cta_name: 'signup_pre_intent',
                  cta_raw_text: 'sign up',
                  cta_location: 'zapier product module',
                  cta_goal: 'signup',
                  signup_type: '',
                  link_destination: '/sign-up',
                });
              }}
              isFullWidth={true}
              size="medium"
            >
              {buttonText}
            </Button>
            <LinkWrapper>
              <Link
                href="/how-it-works"
                onClick={(event) => {
                  emitCTAClickEvent({
                    option_selected: OptionSelected.cta_link,
                    interaction_raw_text: linkText,
                    destination_url_address: '/how-it-works',
                    custom_tracking_fields: {
                      component: TrackedComponent.ZapierProductModule,
                    },
                  });
                  trackEvent('linkClicked', {
                    linkText,
                    href: '/how-it-works',
                    path: window.location.pathname,
                    posYFromTop: event.clientY,
                  });
                }}
              >
                {linkText}
              </Link>
            </LinkWrapper>
          </CTAWrapper>
        </Content>
        <Image
          alt="A Zap with the trigger 'When I get a new lead from Facebook,' and the action 'Notify my team in Slack'"
          src={backgroundArtwork}
        />
      </Container>
    </TrackingWrapper>
  );
};

const Outer = styled.section`
  display: flex;
  background-color: ${Colors.cream};
  justify-content: center;
  text-align: center;
  width: 100%;
  ${mediaQueries.tablet} {
    text-align: left;
  }
`;

const TrackingWrapper = withScrollTracking(Outer);

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
`;

const Content = styled.div`
  padding: 50px 0 60px;

  ${mediaQueries.tablet} {
    max-width: 396px;
  }

  ${mediaQueries.desktop} {
    max-width: 600px;
    padding: 128px 0 128px;
  }
`;

const Text = styled.h2`
  color: ${Colors.neutral800};
  ${Typography.pageHeader9}
  margin-bottom: 40px;

  ${mediaQueries.tablet} {
    ${Typography.pageHeader5}
  }
`;

const CTAWrapper = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: 193px;
  justify-content: center;
  justify-items: center;

  ${mediaQueries.tablet} {
    grid-gap: 25px;
    grid-template-columns: 175px max-content;
    justify-content: start;
  }
`;

const LinkWrapper = styled.div`
  color: ${Colors.neutral800};
  font-size: 18px;
  font-weight: 600;
  line-height: 25px;
  text-decoration: underline;

  ${mediaQueries.tablet} {
    align-self: center;
  }

  &:hover,
  &:focus {
    color: ${Colors.blueJeans};
  }
`;

const Image = styled.img`
  display: none;

  ${mediaQueries.tablet} {
    display: block;
    width: 55%;
    margin-right: -10%;
    margin-bottom: 48px;
  }

  ${mediaQueries.desktop} {
    width: 45%;
    margin-right: -5%;
    margin-bottom: 0;
  }

  @media (min-width: 1200px) {
    margin-right: 0;
  }
`;
